import * as Yup from 'yup';
import { OCPI_COMMANDS_ARRAY } from './ocpi.constants';

// OCPI Token validation schema
export const tokenValidationSchema = Yup.object({
  countryCode: Yup.string().required('Country Code is required').length(2, 'Must be 2 characters'),
  partyId: Yup.string().required('Party ID is required').max(3, 'Must be 3 characters or less'),
  uid: Yup.string().required('Token UID is required'),
  type: Yup.string().required('Token Type is required'),
  whitelist: Yup.string().required('Whitelist Type is required'),
  contractId: Yup.string().required('Contract ID is required'),
  issuer: Yup.string().required('Issuer is required'),
  visualNumber: Yup.string().nullable(),
  groupId: Yup.string().nullable(),
  language: Yup.string().required('Language is required'),
  defaultProfileType: Yup.string().required('Default Profile Type is required'),
  energyContract: Yup.object({
    supplierName: Yup.string().nullable(),
    contractId: Yup.string().nullable()
  }).nullable(),
  valid: Yup.boolean()
});

// OCPI Tariff validation schema
export const tariffValidationSchema = Yup.object({
  countryCode: Yup.string().required('Country Code is required').length(2, 'Must be 2 characters'),
  partyId: Yup.string().required('Party ID is required').max(3, 'Must be 3 characters or less'),
  id: Yup.string().required('Tariff ID is required'),
  currency: Yup.string().required('Currency is required'),
  type: Yup.string().required('Tariff Type is required'),
  energy_category: Yup.string().required('Energy Category is required'),
  restrictions: Yup.string().required('Restrictions are required'),
  description: Yup.string().nullable(),
  min_price: Yup.number().min(0, 'Minimum price must be 0 or greater').nullable(),
  max_price: Yup.number().min(0, 'Maximum price must be 0 or greater').nullable(),
  valid: Yup.boolean(),
  elements: Yup.array().of(
    Yup.object({
      price_components: Yup.array().of(
        Yup.object({
          type: Yup.string().required('Price component type is required'),
          price: Yup.number().min(0, 'Price must be 0 or greater').required('Price is required'),
          step_size: Yup.number().min(0.1, 'Step size must be 0.1 or greater').required('Step size is required')
        })
      ).min(1, 'At least one price component is required')
    })
  ).min(1, 'At least one pricing element is required')
});

// OCPI Command validation schema
export const commandValidationSchema = Yup.object({
  command: Yup.string().required('Command type is required'),
  locationId: Yup.string().when('command', {
    is: (command) => ['START_SESSION', 'STOP_SESSION', 'RESERVE_NOW', 'UNLOCK_CONNECTOR'].includes(command),
    then: Yup.string().required('Location ID is required'),
    otherwise: Yup.string().nullable()
  }),
  evseUid: Yup.string().nullable(),
  connectorId: Yup.string().nullable(),
  authorizationReference: Yup.string().nullable(),
  reservationId: Yup.string().nullable(),
  sessionId: Yup.string().nullable(),
  responseUrl: Yup.string().url('Must be a valid URL').nullable(),
  notes: Yup.string().nullable()
});

// Command validation schema by type
export const getCommandValidationSchema = (commandType) => {
  let schema = Yup.object({
    command_type: Yup.string().required('Command type is required'),
    evse_uid: Yup.string().nullable(),
    connector_id: Yup.string().nullable(),
    authorization_reference: Yup.string().nullable(),
    reservation_id: Yup.string().nullable(),
    session_id: Yup.string().nullable(),
    notes: Yup.string().nullable()
  });

  // Add type-specific validation
  switch (commandType) {
    case 'START_SESSION':
      schema = schema.concat(Yup.object({
        location_id: Yup.string().required('Location is required'),
        evse_uid: Yup.string().nullable(),
        connector_id: Yup.string().nullable(),
        authorization_reference: Yup.string().nullable(),
        token_id: Yup.string().required('Token is required'),
      }));
      break;
    case 'STOP_SESSION':
      schema = schema.concat(Yup.object({
        session_id: Yup.string().nullable().required('Session ID is required'),
      }));
      break;
    case 'RESERVE_NOW':
      schema = schema.concat(Yup.object({
        evse_uid: Yup.string().nullable(),
        location_id: Yup.string().required('Location is required'),
        connector_id: Yup.string().nullable(),
        authorization_reference: Yup.string().nullable(),
        reservation_id: Yup.string().nullable()
      }));
      break;
    case 'UNLOCK_CONNECTOR':
      schema = schema.concat(Yup.object({
        location_id: Yup.string().required('Location is required'),
        evse_uid: Yup.string().nullable().required('EVSE UID is required'),
        connector_id: Yup.string().nullable().required('Connector ID is required')
      }));
      break;
    case 'CANCEL_RESERVATION':
      schema = schema.concat(Yup.object({
        reservation_id: Yup.string().nullable()
      }));
      break;
  }

  return schema;
};

// Partner validation schema
export const partnerValidationSchema = Yup.object({
  partnerName: Yup.string().required('Partner Name is required'),
  partyId: Yup.string().required('Party ID is required').max(3, 'Must be 3 characters or less'),
  countryCode: Yup.string().required('Country Code is required').length(2, 'Must be 2 characters'),
  baseUrl: Yup.string().url('Must be a valid URL').required('Base URL is required'),
  roles: Yup.array().of(Yup.string()).min(1, 'At least one role is required'),
  version: Yup.string().required('Version is required')
});

// Generic form validation schemas
export const searchValidationSchema = Yup.object({
  searchTerm: Yup.string().min(2, 'Search term must be at least 2 characters')
});

export const paginationValidationSchema = Yup.object({
  offset: Yup.number().min(0, 'Offset must be 0 or greater'),
  limit: Yup.number().min(1, 'Limit must be 1 or greater').max(1000, 'Limit cannot exceed 1000')
});

// Export all schemas
export default {
  tokenValidationSchema,
  commandValidationSchema,
  partnerValidationSchema,
  searchValidationSchema,
  paginationValidationSchema
}; 